import React from 'react';
import { Link } from 'react-router-dom';
import { DealProps } from '../../types/deal';
import OptimizedImage from '../common/OptimizedImage';
import { resolveImageUrl } from '../../utils/imageUtils';
import { generateDealUrl } from '../../utils/dealUrlHelper';
import './CompactDealCard.css';

interface CompactDealCardProps {
  deal: DealProps;
}

const CompactDealCard: React.FC<CompactDealCardProps> = ({ deal }) => {
  const formatPrice = (price: number | undefined) => {
    if (!price) return 'Free';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(price);
  };

  const getImageSource = () => {
    // Try new optimized images structure first (prefer small thumbnails for compact cards)
    if (deal.optimizedImages && deal.optimizedImages.length > 0) {
      const primaryImage = deal.optimizedImages[0];

      // Use small thumbnails for compact cards (200x200 square)
      if (primaryImage.thumbnails?.small) {
        return {
          src: resolveImageUrl(primaryImage.thumbnails.small.jpeg),
          avif: resolveImageUrl(primaryImage.thumbnails.small.avif),
          webp: resolveImageUrl(primaryImage.thumbnails.small.webp),
          width: 80,
          height: 60,
          alt: deal.title,
        };
      }

      // Fallback to medium thumbnails
      if (primaryImage.thumbnails?.medium) {
        return {
          src: resolveImageUrl(primaryImage.thumbnails.medium.jpeg),
          avif: resolveImageUrl(primaryImage.thumbnails.medium.avif),
          webp: resolveImageUrl(primaryImage.thumbnails.medium.webp),
          width: 80,
          height: 60,
          alt: deal.title,
        };
      }

      // Fallback to large thumbnails
      if (primaryImage.thumbnails?.large) {
        return {
          src: resolveImageUrl(primaryImage.thumbnails.large.jpeg),
          avif: resolveImageUrl(primaryImage.thumbnails.large.avif),
          webp: resolveImageUrl(primaryImage.thumbnails.large.webp),
          width: 80,
          height: 60,
          alt: deal.title,
        };
      }

      // Fallback to original optimized image
      return {
        src: resolveImageUrl(primaryImage.jpeg),
        avif: resolveImageUrl(primaryImage.avif),
        webp: resolveImageUrl(primaryImage.webp),
        width: 80,
        height: 60,
        alt: deal.title,
      };
    }

    // Fall back to legacy image formats
    if (deal.imageUrl) {
      return {
        src: resolveImageUrl(deal.imageUrl),
        width: 80,
        height: 60,
        alt: deal.title,
      };
    }
    if (deal.images && deal.images.length > 0) {
      return {
        src: resolveImageUrl(deal.images[0]),
        width: 80,
        height: 60,
        alt: deal.title,
      };
    }

    return null;
  };

  const discountPercentage =
    deal.discountPercent ||
    (deal.retailPrice && deal.price
      ? Math.round(((deal.retailPrice - deal.price) / deal.retailPrice) * 100)
      : 0);

  const imageProps = getImageSource();

  return (
    <div className="compact-deal-card">
      <Link to={generateDealUrl(deal)} className="compact-deal-link">
        <div className="compact-deal-image">
          {imageProps ? (
            <OptimizedImage
              src={imageProps.src}
              alt={imageProps.alt}
              width={imageProps.width}
              height={imageProps.height}
              className="compact-image"
            />
          ) : (
            <div className="compact-image-placeholder">📦</div>
          )}
          {discountPercentage > 0 && (
            <span className="compact-discount-badge">{discountPercentage}% OFF</span>
          )}
        </div>

        <div className="compact-deal-content">
          <h4 className="compact-deal-title">{deal.title}</h4>
          <div className="compact-deal-pricing">
            <span className="compact-current-price">{formatPrice(deal.price)}</span>
            {deal.retailPrice && deal.retailPrice > (deal.price || 0) && (
              <span className="compact-original-price">{formatPrice(deal.retailPrice)}</span>
            )}
          </div>
          {deal.store && typeof deal.store === 'object' && (
            <div className="compact-store-name">{deal.store.name}</div>
          )}
        </div>
      </Link>
    </div>
  );
};

export default CompactDealCard;
